/**
 * Estate Flow - Property Management Platform
 * Copyright © 2025 Sebastian Alliance Group LLC. All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL
 * 
 * This software and associated documentation files (the "Software") are the 
 * proprietary property of Sebastian Alliance Group LLC and are protected by 
 * copyright, trademark, and other intellectual property laws.
 * 
 * Unauthorized copying, modification, distribution, or use of this Software,
 * via any medium, is strictly prohibited without express written permission
 * from Sebastian Alliance Group LLC.
 * 
 * For licensing inquiries: legal@sebastianalliancegroup.com
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Building2, 
  Users, 
  DollarSign, 
  TrendingUp, 
  Settings,
  LogOut,
  Plus,
  Eye,
  Edit,
  Shield,
  CreditCard,
  FileText,
  BarChart3,
  UserCheck,
  AlertCircle,
  CheckCircle,
  Wallet,
  HelpCircle,
  Trash2
} from 'lucide-react';
import FinanceDashboard from './admin/FinanceDashboard';
import CustomPricingManager from './admin/CustomPricingManager';
import SecuritySettings from './admin/SecuritySettings';
import SystemHealthPanel from './admin/SystemHealthPanel';
import PaymentTestingModule from './admin/PaymentTestingModule';
import ReferralDashboard from './ReferralDashboard';
import PromoCodeManager from './admin/PromoCodeManager';
import SubscriptionsManager from './admin/SubscriptionsManager';
import ReferralPartnersManager from './admin/ReferralPartnersManager';
import FinancingLeadsManager from './admin/FinancingLeadsManager';
import ConversionAnalytics from './admin/ConversionAnalytics';
import PayoutManagement from './admin/PayoutManagement';
import SystemConfigManager from './admin/SystemConfigManager';
import TaxManagement from './admin/TaxManagement';
import UserDirectory from './UserDirectory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';
  const [loading, setLoading] = useState(true);
  const [adminData, setAdminData] = useState({
    stats: {
      total_organizations: 0,
      total_properties: 0,
      total_tenants: 0,
      total_revenue: 0,
      pending_approvals: 0
    },
    recent_organizations: [],
    recent_payments: [],
    system_health: 'healthy'
  });
  const [activeTab, setActiveTab] = useState(initialTab);

  // Sync tab to URL
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };
  const [platformRates, setPlatformRates] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [transactionFilters, setTransactionFilters] = useState({
    type: 'all',
    timeRange: 'month'
  });
  const [editRateDialog, setEditRateDialog] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [paymentGateways, setPaymentGateways] = useState([]);
  const [editGatewayDialog, setEditGatewayDialog] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);
  
  // Create User state
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'hoa_admin',
    phone: ''
  });
  const [creatingUser, setCreatingUser] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userRole = localStorage.getItem('user_role');
    
    if (!token || !['admin', 'platform_super_admin'].includes(userRole)) {
      navigate('/admin/login');
      return;
    }
    
    fetchAdminData();
  }, [navigate]);

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'rates' && !platformRates) {
      fetchPlatformRates();
    } else if (activeTab === 'payments') {
      fetchPaymentGateways();
      fetchPaymentProcessor();
    } else if (activeTab === 'transactions') {
      fetchTransactions();
    } else if (activeTab === 'audit') {
      fetchAuditLogs();
    }
  }, [activeTab]);

  // Re-fetch transactions when filters change
  useEffect(() => {
    if (activeTab === 'transactions') {
      fetchTransactions();
    }
  }, [transactionFilters]);

  const viewOrgDetail = async (org) => {
    setSelectedOrg(org);
    setOrgLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`${API}/admin/organizations/${org.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedOrg({ ...org, ...res.data });
    } catch (e) {
      // If endpoint fails, just show what we have
    }
    setOrgLoading(false);
  };

  const fetchAdminData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch real dashboard stats from backend
      const statsResponse = await axios.get(`${API}/admin/dashboard-stats`, { headers });
      const statsData = statsResponse.data;
      
      setAdminData({
        stats: {
          total_organizations: statsData.user_counts?.total_organizations || 0,
          total_properties: statsData.property_counts?.total_properties || 0,
          total_tenants: statsData.user_counts?.tenants || 0,
          total_revenue: statsData.financial?.total_revenue || 0,
          pending_approvals: statsData.financial?.pending_approvals || 0,
          user_counts: statsData.user_counts || {}
        },
        recent_organizations: statsData.recent_organizations || [],
        recent_payments: statsData.recent_payments || [],
        recent_activity: statsData.recent_activity || [],
        system_health: 'healthy'
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  const fetchPlatformRates = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/platform-rates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPlatformRates(response.data);
    } catch (error) {
      console.error('Error fetching platform rates:', error);
      toast.error('Failed to load platform rates');
    }
  };

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/transactions`, {
        params: {
          transaction_type: transactionFilters.type,
          time_range: transactionFilters.timeRange
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transactions');
    }
  };

  const fetchPaymentProcessor = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/payment-processor`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAdminData(prev => ({
        ...prev,
        activeProcessor: response.data.active_processor
      }));
    } catch (error) {
      console.error('Error fetching payment processor:', error);
      toast.error('Failed to load payment processor status');
    }
  };

  const handleSetPaymentProcessor = async (processor) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/admin/payment-processor`,
        { processor },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setAdminData(prev => ({
        ...prev,
        activeProcessor: processor
      }));
      
      toast.success(`Payment processor switched to ${processor.toUpperCase()}`, {
        description: `All new transactions will now be processed through ${processor === 'stripe' ? 'Stripe' : 'PlugnPay'}`
      });
    } catch (error) {
      console.error('Error setting payment processor:', error);
      toast.error('Failed to switch payment processor');
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/audit-log`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAuditLogs(response.data.audit_logs || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to load audit logs');
    }
  };

  const handleUpdateRates = async (updatedRates) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/admin/update-rates`, updatedRates, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Platform rates updated successfully');
      fetchPlatformRates();
      fetchAuditLogs();
      setEditRateDialog(false);
    } catch (error) {
      console.error('Error updating rates:', error);
      toast.error('Failed to update rates');
    }
  };

  const handleExportTransactions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/transactions/export`, {
        params: {
          transaction_type: transactionFilters.type,
          time_range: transactionFilters.timeRange,
          format: 'csv'
        },
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `estate_flow_transactions_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Transactions exported successfully');
    } catch (error) {
      console.error('Error exporting transactions:', error);
      toast.error('Failed to export transactions');
    }
  };

  const handleRefundTransaction = async (transaction) => {
    const confirmed = window.confirm(
      `Are you sure you want to refund $${(transaction.amount || 0).toFixed(2)} to ${transaction.from}?\n\nOrder ID: ${transaction.order_id}\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    const reason = prompt('Please enter a reason for the refund (optional):');
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/admin/payments/refund`,
        {
          order_id: transaction.order_id,
          amount: null, // Full refund
          reason: reason || 'Admin refund'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Transaction refunded successfully', {
          description: `Refunded $${(transaction.amount || 0).toFixed(2)} to ${transaction.from}`
        });
        // Refresh transactions
        fetchTransactions();
      } else {
        toast.error('Refund failed', {
          description: response.data.message || 'Unable to process refund'
        });
      }
    } catch (error) {
      console.error('Error refunding transaction:', error);
      toast.error('Failed to process refund', {
        description: error.response?.data?.detail || 'An error occurred'
      });
    }
  };

  const handleVoidTransaction = async (transaction) => {
    const confirmed = window.confirm(
      `Are you sure you want to void this transaction?\n\nOrder ID: ${transaction.order_id}\nAmount: $${(transaction.amount || 0).toFixed(2)}\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/admin/payments/void`,
        {
          order_id: transaction.order_id
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Transaction voided successfully', {
          description: `Order ${transaction.order_id.substring(0, 12)} has been voided`
        });
        // Refresh transactions
        fetchTransactions();
      } else {
        toast.error('Void failed', {
          description: response.data.message || 'Unable to void transaction'
        });
      }
    } catch (error) {
      console.error('Error voiding transaction:', error);
      toast.error('Failed to void transaction', {
        description: error.response?.data?.detail || 'An error occurred'
      });
    }
  };

  const fetchPaymentGateways = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/payment-gateways`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPaymentGateways(response.data.gateways || []);
    } catch (error) {
      console.error('Error fetching payment gateways:', error);
      toast.error('Failed to load payment gateways');
    }
  };

  const handleConfigureGateway = async (gatewayType, credentials, isActive) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/admin/payment-gateways/${gatewayType}`, {
        gateway_type: gatewayType,
        is_active: isActive,
        credentials: credentials
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Payment gateway configured successfully');
      fetchPaymentGateways();
      setEditGatewayDialog(false);
    } catch (error) {
      console.error('Error configuring gateway:', error);
      toast.error('Failed to configure payment gateway');
    }
  };

  const handleToggleGateway = async (gatewayType, isActive) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/admin/payment-gateways/${gatewayType}/toggle?is_active=${isActive}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`Gateway ${isActive ? 'activated' : 'deactivated'} successfully`);
      fetchPaymentGateways();
    } catch (error) {
      console.error('Error toggling gateway:', error);
      toast.error(error.response?.data?.detail || 'Failed to toggle gateway');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_id');
    navigate('/admin/login');
  };

  // Create User function
  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password || !newUser.role) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCreatingUser(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/admin/create-user`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`User ${newUser.name} created successfully!`);
        setShowCreateUserDialog(false);
        setNewUser({ name: '', email: '', password: '', role: 'hoa_admin', phone: '' });
        fetchAdminData(); // Refresh data
      } else {
        toast.error(response.data.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
    setCreatingUser(false);
  };

  // Delete User function
  const handleDeleteUser = async (email, name) => {
    if (!window.confirm(`Are you sure you want to delete user "${name}" (${email})?\n\nThis will PERMANENTLY remove them from ALL collections.\n\nThis action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.delete(`${API}/admin/delete-user/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`User ${email} deleted from: ${response.data.deleted_from?.join(', ') || 'database'}`);
        fetchAdminData(); // Refresh data
      } else {
        toast.error(response.data.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Find/Search User function (debug tool)
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  
  const handleSearchUser = async () => {
    if (!searchEmail.trim()) {
      toast.error('Please enter an email to search');
      return;
    }
    
    setSearching(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/find-user/${encodeURIComponent(searchEmail.trim())}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSearchResult(response.data);
      if (response.data.found) {
        toast.success(`Found in ${response.data.collections.length} collection(s): ${response.data.collections.join(', ')}`);
      } else {
        toast.info('Email not found in any collection');
      }
    } catch (error) {
      console.error('Error searching user:', error);
      toast.error(error.response?.data?.detail || 'Search failed');
      setSearchResult(null);
    }
    setSearching(false);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Building2 className="h-8 w-8 text-emerald-600" />
              <div>
                <span className="text-2xl font-bold text-gray-900">Estate Flow</span>
                <p className="text-xs text-gray-500 -mt-1">Admin Portal</p>
              </div>
              <Badge className="bg-emerald-100 text-emerald-800">
                <Shield className="h-3 w-3 mr-1" />
                Administrator
              </Badge>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  adminData.system_health === 'healthy' ? 'bg-green-500' : 'bg-red-500'
                }`} />
                <span className="text-sm text-gray-600">System Status: {adminData.system_health}</span>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/kb')}
                data-testid="admin-dashboard-help-btn"
              >
                <HelpCircle className="h-4 w-4 mr-2" />
                Help
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleLogout}
                data-testid="admin-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Estate Flow Admin Dashboard
          </h1>
          <p className="text-gray-600">
            Monitor platform performance, manage organizations, and oversee operations
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card data-testid="total-orgs-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Organizations</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{adminData.stats.total_organizations}</div>
              <p className="text-xs text-muted-foreground">Active organizations</p>
            </CardContent>
          </Card>
          
          <Card data-testid="total-properties-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Properties</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{adminData.stats.total_properties}</div>
              <p className="text-xs text-muted-foreground">Total properties</p>
            </CardContent>
          </Card>
          
          <Card data-testid="total-tenants-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tenants</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{adminData.stats.total_tenants}</div>
              <p className="text-xs text-muted-foreground">Active tenants</p>
            </CardContent>
          </Card>
          
          <Card data-testid="total-revenue-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(adminData.stats.total_revenue)}</div>
              <p className="text-xs text-muted-foreground">This month</p>
            </CardContent>
          </Card>
          
          <Card data-testid="pending-approvals-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <AlertCircle className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{adminData.stats.pending_approvals}</div>
              <p className="text-xs text-muted-foreground">Need approval</p>
            </CardContent>
          </Card>
        </div>

        {/* Admin Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="grid grid-cols-10 w-full">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="conversion" className="text-orange-600">📊 Conversion</TabsTrigger>
            <TabsTrigger value="rates">Rate Management</TabsTrigger>
            <TabsTrigger value="custom-pricing">Custom Pricing</TabsTrigger>
            <TabsTrigger value="payments">Payment Settings</TabsTrigger>
            <TabsTrigger value="payment-testing">Payment Testing</TabsTrigger>
            <TabsTrigger value="finance">Finance</TabsTrigger>
            <TabsTrigger value="users">User Oversight</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="referrals">Referrals</TabsTrigger>
            <TabsTrigger value="partners">Partners</TabsTrigger>
            <TabsTrigger value="promo-codes">Promo Codes</TabsTrigger>
            <TabsTrigger value="financing">🏦 Financing</TabsTrigger>
            <TabsTrigger value="payouts" className="text-emerald-600">💰 Payouts</TabsTrigger>
            <TabsTrigger value="tax-pl" className="text-purple-600">📊 Tax & P&L</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="credentials" className="text-orange-600">🔐 Credentials</TabsTrigger>
            <TabsTrigger value="system-health">System Health</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Organizations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Building2 className="h-5 w-5 mr-2" />
                    Recent Organizations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {adminData.recent_organizations.map((org, index) => (
                      <div key={org.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex-1">
                          <h4 className="font-medium">{org.name}</h4>
                          <p className="text-sm text-gray-600 capitalize">{org.type}</p>
                          <p className="text-xs text-gray-500">{new Date(org.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge className={getStatusColor(org.status)}>
                            {org.status}
                          </Badge>
                          <Button variant="outline" size="sm" onClick={() => viewOrgDetail(org)}>
                            <Eye className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Payments */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <CreditCard className="h-5 w-5 mr-2" />
                    Recent Payments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {adminData.recent_payments.map((payment, index) => (
                      <div key={payment.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex-1">
                          <h4 className="font-medium">{formatCurrency(payment.amount)}</h4>
                          <p className="text-sm text-gray-600">{payment.tenant}</p>
                          <p className="text-xs text-gray-500">{payment.property}</p>
                        </div>
                        <Badge className={getStatusColor(payment.status)}>
                          {payment.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Conversion Analytics Tab - CRITICAL FOR TRACKING SALES FUNNEL */}
          <TabsContent value="conversion" className="space-y-6">
            <ConversionAnalytics />
          </TabsContent>
          
          <TabsContent value="rates" className="space-y-6">
            {/* Rate Management */}
            {!platformRates ? (
              <div className="flex justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Subscription Rates</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium">Landlord Monthly Fee</h4>
                          <p className="text-sm text-gray-600">
                            Current: ${platformRates.subscription_rates?.landlord?.min_monthly} - ${platformRates.subscription_rates?.landlord?.max_monthly}/month
                          </p>
                          <p className="text-xs text-gray-500">Setup: ${platformRates.subscription_rates?.landlord?.setup_fee}</p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setEditingRate({type: 'landlord', data: platformRates.subscription_rates.landlord});
                            setEditRateDialog(true);
                          }}
                        >
                          <Edit className="h-3 w-3 mr-2" />
                          Edit
                        </Button>
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium">HOA Monthly Fee</h4>
                          <p className="text-sm text-gray-600">
                            Current: ${platformRates.subscription_rates?.hoa?.min_monthly} - ${platformRates.subscription_rates?.hoa?.max_monthly}/month
                          </p>
                          <p className="text-xs text-gray-500">Setup: ${platformRates.subscription_rates?.hoa?.setup_fee}</p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setEditingRate({type: 'hoa', data: platformRates.subscription_rates.hoa});
                            setEditRateDialog(true);
                          }}
                        >
                          <Edit className="h-3 w-3 mr-2" />
                          Edit
                        </Button>
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium">Property Manager Fee</h4>
                          <p className="text-sm text-gray-600">Current: ${platformRates.subscription_rates?.property_manager?.monthly}/month</p>
                          <p className="text-xs text-gray-500">Setup: ${platformRates.subscription_rates?.property_manager?.setup_fee}</p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setEditingRate({type: 'property_manager', data: platformRates.subscription_rates.property_manager});
                            setEditRateDialog(true);
                          }}
                        >
                          <Edit className="h-3 w-3 mr-2" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Commission Rates</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium">Platform Commission</h4>
                          <p className="text-sm text-gray-600">Current: {platformRates.commission_rates?.platform_percentage}% per transaction</p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setEditingRate({type: 'platform_commission', data: platformRates.commission_rates});
                            setEditRateDialog(true);
                          }}
                        >
                          <Edit className="h-3 w-3 mr-2" />
                          Adjust
                        </Button>
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium">Distribution Fee</h4>
                          <p className="text-sm text-gray-600">Current: ${(platformRates.commission_rates?.distribution_fee_cents || 0) / 100} per payout</p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setEditingRate({type: 'distribution_fee', data: platformRates.commission_rates});
                            setEditRateDialog(true);
                          }}
                        >
                          <Edit className="h-3 w-3 mr-2" />
                          Adjust
                        </Button>
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium">Property Manager Commission</h4>
                          <p className="text-sm text-gray-600">Current: {platformRates.commission_rates?.property_manager_percentage}% per transaction</p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setEditingRate({type: 'pm_commission', data: platformRates.commission_rates});
                            setEditRateDialog(true);
                          }}
                        >
                          <Edit className="h-3 w-3 mr-2" />
                          Adjust
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="custom-pricing" className="space-y-6">
            <CustomPricingManager />
          </TabsContent>

          
          <TabsContent value="payments" className="space-y-6">
            {/* Active Payment Processor Selection */}
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CreditCard className="h-5 w-5 text-blue-600" />
                  <span>Active Payment Processor</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-700 mb-4">
                  Stripe is currently the sole payment processor for all transactions. Additional processors may be added in the future.
                </p>
                
                <div className="grid grid-cols-1 gap-4">
                  {/* Stripe - Active */}
                  <div 
                    className="border-2 rounded-lg p-4 border-blue-500 bg-blue-50"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <span className="text-3xl">💳</span>
                        <div>
                          <h4 className="font-semibold text-lg">Stripe</h4>
                          <p className="text-xs text-gray-500">Credit/Debit Cards • Live Mode</p>
                        </div>
                      </div>
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="text-xs text-gray-600 mt-2">
                      Global payment processing with automatic fraud detection, recurring billing, and instant payouts.
                    </div>
                    <div className="mt-3 flex items-center space-x-2">
                      <Badge className="bg-green-100 text-green-800 text-xs">ACTIVE</Badge>
                      <Badge className="bg-blue-100 text-blue-800 text-xs">LIVE KEYS</Badge>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Active processor: <strong className="text-green-700">Stripe</strong></span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchPaymentProcessor()}
                  >
                    Refresh Status
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Payment Gateway Configuration */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Gateway Configuration</h2>
              <p className="text-gray-600">
                Configure and manage payment processing gateways. All credentials are AES-256 encrypted.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {paymentGateways.map((gateway) => {
                const configured = gateway.credentials && Object.keys(gateway.credentials).length > 0;
                const gatewayIcons = {
                  wise: '🏦',
                  stripe: '💳',
                  cybersource: '🔐',
                  plugnpay: '⚡',
                  currencycloud: '🌐'
                };

                return (
                  <Card key={gateway.gateway_type || gateway.display_name}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-2xl">{gatewayIcons[gateway.gateway_type] || '💰'}</span>
                          <CardTitle>{gateway.display_name}</CardTitle>
                        </div>
                        <div className="flex items-center space-x-2">
                          {configured && (
                            <Badge className={gateway.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                              {gateway.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          )}
                          {!configured && (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              Not Configured
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Instant Split Support:</span>
                          <span className="font-medium">
                            {gateway.supports_instant_split ? '✅ Yes' : '❌ No'}
                          </span>
                        </div>
                        {configured && gateway.credentials && (
                          <div className="mt-4">
                            <p className="text-xs text-gray-500 mb-2">Configured Credentials:</p>
                            <div className="space-y-1">
                              {Object.entries(gateway.credentials).map(([key, value]) => (
                                <div key={key} className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                                  <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                                  <code className="text-gray-800">{value}</code>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex space-x-2 pt-4 border-t">
                        <Button 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => {
                            setEditingGateway(gateway);
                            setEditGatewayDialog(true);
                          }}
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          {configured ? 'Edit' : 'Configure'}
                        </Button>
                        {configured && (
                          <Button
                            variant={gateway.is_active ? 'destructive' : 'default'}
                            className={!gateway.is_active ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                            onClick={() => handleToggleGateway(gateway.gateway_type, !gateway.is_active)}
                          >
                            {gateway.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Security Notice */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-3">
                  <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900 mb-1">Security Information</h4>
                    <p className="text-sm text-blue-800">
                      All payment gateway credentials are encrypted using AES-256 encryption before storage. 
                      Only Estate Flow Administrators can view or modify these settings. All changes are logged 
                      in the audit trail for compliance and security purposes.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payment-testing" className="space-y-6">
            <PaymentTestingModule />
          </TabsContent>

          <TabsContent value="finance" className="space-y-6">
            <FinanceDashboard />
          </TabsContent>
          
          <TabsContent value="users" className="space-y-6">
            {/* User Oversight — Full Directory */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold" data-testid="user-oversight-title">User Oversight</h2>
              <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-user-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create User
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="user-name">Full Name *</Label>
                      <Input
                        id="user-name"
                        placeholder="e.g., Racquel Smith"
                        value={newUser.name}
                        onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="user-email">Email *</Label>
                      <Input
                        id="user-email"
                        type="email"
                        placeholder="e.g., user@example.com"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="user-password">Password *</Label>
                      <Input
                        id="user-password"
                        type="password"
                        placeholder="Min 8 characters"
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="user-phone">Phone</Label>
                      <Input
                        id="user-phone"
                        placeholder="e.g., +1234567890"
                        value={newUser.phone}
                        onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="user-role">Role *</Label>
                      <Select
                        value={newUser.role}
                        onValueChange={(value) => setNewUser({...newUser, role: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hoa_admin">HOA Administrator</SelectItem>
                          <SelectItem value="hoa_owner">HOA Owner</SelectItem>
                          <SelectItem value="landlord">Landlord</SelectItem>
                          <SelectItem value="property_manager">Property Manager</SelectItem>
                          <SelectItem value="vacation_rental">Vacation Rental Manager</SelectItem>
                          <SelectItem value="tenant">Tenant</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleCreateUser}
                      disabled={creatingUser}
                    >
                      {creatingUser ? 'Creating...' : 'Create User'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <UserDirectory stats={adminData.stats} onRefresh={fetchAdminData} />
          </TabsContent>
          
          <TabsContent value="transactions" className="space-y-6">
            {/* Transaction Monitoring */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Transaction Monitoring</CardTitle>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={handleExportTransactions}>
                      Export CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Filters */}
                  <div className="flex space-x-4">
                    <Select 
                      value={transactionFilters.type}
                      onValueChange={(value) => setTransactionFilters({...transactionFilters, type: value})}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Transaction Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Transactions</SelectItem>
                        <SelectItem value="rent">Rent Payments</SelectItem>
                        <SelectItem value="hoa">HOA Fees</SelectItem>
                        <SelectItem value="subscription">Subscriptions</SelectItem>
                        <SelectItem value="payout">Payouts</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select 
                      value={transactionFilters.timeRange}
                      onValueChange={(value) => setTransactionFilters({...transactionFilters, timeRange: value})}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Time Range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="today">Today</SelectItem>
                        <SelectItem value="week">This Week</SelectItem>
                        <SelectItem value="month">This Month</SelectItem>
                        <SelectItem value="quarter">This Quarter</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Transaction Table with Actions */}
                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-3">Order ID</th>
                          <th className="text-left p-3">Date/Time</th>
                          <th className="text-left p-3">Type</th>
                          <th className="text-left p-3">From</th>
                          <th className="text-left p-3">Amount</th>
                          <th className="text-left p-3">Fee</th>
                          <th className="text-left p-3">Status</th>
                          <th className="text-left p-3">Processor</th>
                          <th className="text-left p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="p-8 text-center text-gray-500">
                              No transactions found for the selected filters
                            </td>
                          </tr>
                        ) : (
                          transactions.map((txn) => (
                            <tr key={txn.id} className="border-t hover:bg-gray-50">
                              <td className="p-3 font-mono text-xs">{txn.order_id?.substring(0, 12) || 'N/A'}</td>
                              <td className="p-3">{new Date(txn.created_at).toLocaleString()}</td>
                              <td className="p-3 capitalize">{txn.type?.replace('_', ' ')}</td>
                              <td className="p-3">{txn.from}</td>
                              <td className="p-3 font-medium">${((txn.amount_cents || txn.amount * 100 || 0) / 100).toFixed(2)}</td>
                              <td className="p-3 text-emerald-600">${((txn.fee_cents || 0) / 100).toFixed(2)}</td>
                              <td className="p-3">
                                <Badge className={getStatusColor(txn.status)}>
                                  {txn.status}
                                </Badge>
                              </td>
                              <td className="p-3 uppercase text-xs">{txn.processor || 'stripe'}</td>
                              <td className="p-3">
                                <div className="flex space-x-2">
                                  {txn.can_refund && txn.status === 'completed' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-xs"
                                      onClick={() => handleRefundTransaction(txn)}
                                    >
                                      Refund
                                    </Button>
                                  )}
                                  {txn.can_void && ['authorized', 'pending'].includes(txn.status) && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-xs"
                                      onClick={() => handleVoidTransaction(txn)}
                                    >
                                      Void
                                    </Button>
                                  )}
                                  {txn.status === 'refunded' && (
                                    <Badge variant="secondary" className="text-xs">Refunded</Badge>
                                  )}
                                  {txn.status === 'voided' && (
                                    <Badge variant="secondary" className="text-xs">Voided</Badge>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="audit" className="space-y-6">
            {/* Audit Log */}
            <Card>
              <CardHeader>
                <CardTitle>System Activity Log</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {auditLogs.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      No audit logs available
                    </div>
                  ) : (
                    auditLogs.map((log) => {
                      const severityColor = {
                        high: 'bg-red-500',
                        medium: 'bg-yellow-500',
                        low: 'bg-blue-500'
                      }[log.severity] || 'bg-gray-500';

                      return (
                        <div key={log.id} className="flex items-start space-x-3 p-3 border rounded-lg">
                          <div className={`w-2 h-2 ${severityColor} rounded-full mt-2`}></div>
                          <div className="flex-1">
                            <h4 className="font-medium capitalize">{log.action?.replace('_', ' ')}</h4>
                            <p className="text-sm text-gray-600">{log.description}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(log.timestamp).toLocaleString()} • {log.admin_id || 'System'}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="security" className="space-y-6">
            <SecuritySettings />
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            {/* Admin Settings */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Notification Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">New Registration Alerts</h4>
                      <p className="text-sm text-gray-600">Get notified when new users register</p>
                    </div>
                    <input type="checkbox" className="toggle" defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">High-Value Transaction Alerts</h4>
                      <p className="text-sm text-gray-600">Alert for transactions over $2,500</p>
                    </div>
                    <input type="checkbox" className="toggle" defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Subscription Renewal Alerts</h4>
                      <p className="text-sm text-gray-600">Notify about renewals and failures</p>
                    </div>
                    <input type="checkbox" className="toggle" defaultChecked />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>System Configuration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button variant="outline" className="w-full justify-start">
                    <FileText className="h-4 w-4 mr-2" />
                    Backup Database
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <Settings className="h-4 w-4 mr-2" />
                    System Maintenance
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <Shield className="h-4 w-4 mr-2" />
                    Security Settings
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* System Health Tab */}
          <TabsContent value="system-health" className="space-y-6">
            <SystemHealthPanel />
          </TabsContent>


          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions" className="space-y-6">
            <SubscriptionsManager />
          </TabsContent>

          <TabsContent value="referrals" className="space-y-6">
            <ReferralDashboard />
          </TabsContent>

          <TabsContent value="partners" className="space-y-6">
            <ReferralPartnersManager />
          </TabsContent>

          <TabsContent value="promo-codes" className="space-y-6">
            <PromoCodeManager />
          </TabsContent>

          <TabsContent value="financing" className="space-y-6">
            <FinancingLeadsManager />
          </TabsContent>

          <TabsContent value="payouts" className="space-y-6">
            <PayoutManagement />
          </TabsContent>

          <TabsContent value="tax-pl" className="space-y-6">
            <TaxManagement />
          </TabsContent>

          <TabsContent value="credentials" className="space-y-6">
            <SystemConfigManager />
          </TabsContent>
        </Tabs>
      </main>

      {/* Rate Edit Dialog */}
      <Dialog open={editRateDialog} onOpenChange={setEditRateDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Rate Configuration</DialogTitle>
          </DialogHeader>
          <RateEditForm 
            rateType={editingRate?.type}
            currentData={editingRate?.data}
            platformRates={platformRates}
            onSave={(updatedRates) => {
              handleUpdateRates(updatedRates);
            }}
            onCancel={() => setEditRateDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Gateway Configuration Dialog */}
      <Dialog open={editGatewayDialog} onOpenChange={setEditGatewayDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              Configure {editingGateway?.display_name}
            </DialogTitle>
          </DialogHeader>
          <GatewayConfigForm 
            gateway={editingGateway}
            onSave={(credentials, isActive) => {
              handleConfigureGateway(editingGateway.gateway_type, credentials, isActive);
            }}
            onCancel={() => setEditGatewayDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Organization Detail Modal */}
      <Dialog open={!!selectedOrg} onOpenChange={() => setSelectedOrg(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              {selectedOrg?.name || 'Organization Details'}
            </DialogTitle>
          </DialogHeader>
          {orgLoading ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>
          ) : selectedOrg && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded p-3">
                  <p className="text-xs text-gray-500">Type</p>
                  <p className="font-medium capitalize">{selectedOrg.type || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <p className="text-xs text-gray-500">Status</p>
                  <Badge className={selectedOrg.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                    {selectedOrg.status || 'pending'}
                  </Badge>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <p className="text-xs text-gray-500">Created</p>
                  <p className="font-medium text-sm">{selectedOrg.created_at ? new Date(selectedOrg.created_at).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <p className="text-xs text-gray-500">Country</p>
                  <p className="font-medium">{selectedOrg.country || 'N/A'}</p>
                </div>
              </div>

              {selectedOrg.owner && (
                <div className="border rounded p-3">
                  <h4 className="font-medium text-sm mb-2">Owner</h4>
                  <p className="text-sm">{selectedOrg.owner.first_name} {selectedOrg.owner.last_name}</p>
                  <p className="text-xs text-gray-500">{selectedOrg.owner.email}</p>
                </div>
              )}

              {selectedOrg.owner_email && !selectedOrg.owner && (
                <div className="border rounded p-3">
                  <h4 className="font-medium text-sm mb-2">Owner Email</h4>
                  <p className="text-sm">{selectedOrg.owner_email}</p>
                </div>
              )}

              {(selectedOrg.properties_count > 0 || selectedOrg.units_count > 0 || selectedOrg.tenants_count > 0) && (
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-blue-50 rounded p-2 text-center">
                    <p className="text-lg font-bold text-blue-700">{selectedOrg.properties_count || 0}</p>
                    <p className="text-xs text-blue-600">Properties</p>
                  </div>
                  <div className="bg-green-50 rounded p-2 text-center">
                    <p className="text-lg font-bold text-green-700">{selectedOrg.units_count || 0}</p>
                    <p className="text-xs text-green-600">Units</p>
                  </div>
                  <div className="bg-purple-50 rounded p-2 text-center">
                    <p className="text-lg font-bold text-purple-700">{selectedOrg.tenants_count || 0}</p>
                    <p className="text-xs text-purple-600">Tenants</p>
                  </div>
                </div>
              )}

              {selectedOrg.properties && selectedOrg.properties.length > 0 && (
                <div className="border rounded p-3">
                  <h4 className="font-medium text-sm mb-2">Properties ({selectedOrg.properties.length})</h4>
                  {selectedOrg.properties.map((p, i) => (
                    <div key={i} className="flex justify-between text-xs p-1.5 border-b last:border-0">
                      <span>{p.name || p.address || `Property ${i+1}`}</span>
                      <span className="text-gray-500">{p.unit_count || p.units_count || 0} units</span>
                    </div>
                  ))}
                </div>
              )}

              {selectedOrg.is_trial && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-sm text-yellow-800">
                  Trial Account {selectedOrg.trial_end_date ? ` — Expires: ${new Date(selectedOrg.trial_end_date).toLocaleDateString()}` : ''}
                </div>
              )}

              {selectedOrg.promo_code_used && (
                <p className="text-sm"><span className="text-gray-500">Promo Code:</span> <Badge className="bg-purple-100 text-purple-800">{selectedOrg.promo_code_used}</Badge></p>
              )}

              <div className="border-t pt-3">
                <Button size="sm" variant="outline" onClick={() => setSelectedOrg(null)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Rate Edit Form Component
const RateEditForm = ({ rateType, currentData, platformRates, onSave, onCancel }) => {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (currentData) {
      setFormData(currentData);
    }
  }, [currentData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create updated rates object
    let updatedRates = { ...platformRates };
    
    switch(rateType) {
      case 'landlord':
      case 'hoa':
      case 'property_manager':
        updatedRates.subscription_rates[rateType] = formData;
        break;
      case 'platform_commission':
        updatedRates.commission_rates.platform_percentage = parseFloat(formData.platform_percentage);
        break;
      case 'distribution_fee':
        updatedRates.commission_rates.distribution_fee_cents = parseInt(formData.distribution_fee_cents);
        break;
      case 'pm_commission':
        updatedRates.commission_rates.property_manager_percentage = parseFloat(formData.property_manager_percentage);
        break;
    }
    
    onSave(updatedRates);
  };

  if (!rateType || !currentData) return null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {(rateType === 'landlord' || rateType === 'hoa') && (
        <>
          <div>
            <Label>Minimum Monthly Fee ($)</Label>
            <Input
              type="number"
              value={formData.min_monthly || ''}
              onChange={(e) => setFormData({...formData, min_monthly: parseInt(e.target.value)})}
              required
            />
          </div>
          <div>
            <Label>Maximum Monthly Fee ($)</Label>
            <Input
              type="number"
              value={formData.max_monthly || ''}
              onChange={(e) => setFormData({...formData, max_monthly: parseInt(e.target.value)})}
              required
            />
          </div>
          <div>
            <Label>Setup Fee ($)</Label>
            <Input
              type="number"
              value={formData.setup_fee || ''}
              onChange={(e) => setFormData({...formData, setup_fee: parseInt(e.target.value)})}
              required
            />
          </div>
        </>
      )}

      {rateType === 'property_manager' && (
        <>
          <div>
            <Label>Monthly Fee ($)</Label>
            <Input
              type="number"
              value={formData.monthly || ''}
              onChange={(e) => setFormData({...formData, monthly: parseInt(e.target.value)})}
              required
            />
          </div>
          <div>
            <Label>Setup Fee ($)</Label>
            <Input
              type="number"
              value={formData.setup_fee || ''}
              onChange={(e) => setFormData({...formData, setup_fee: parseInt(e.target.value)})}
              required
            />
          </div>
        </>
      )}

      {rateType === 'platform_commission' && (
        <div>
          <Label>Platform Commission (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={formData.platform_percentage || currentData.platform_percentage || ''}
            onChange={(e) => setFormData({...formData, platform_percentage: e.target.value})}
            required
          />
        </div>
      )}

      {rateType === 'distribution_fee' && (
        <div>
          <Label>Distribution Fee (cents)</Label>
          <Input
            type="number"
            value={formData.distribution_fee_cents || currentData.distribution_fee_cents || ''}
            onChange={(e) => setFormData({...formData, distribution_fee_cents: e.target.value})}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            ${((formData.distribution_fee_cents || currentData.distribution_fee_cents || 0) / 100).toFixed(2)} per payout
          </p>
        </div>
      )}

      {rateType === 'pm_commission' && (
        <div>
          <Label>Property Manager Commission (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={formData.property_manager_percentage || currentData.property_manager_percentage || ''}
            onChange={(e) => setFormData({...formData, property_manager_percentage: e.target.value})}
            required
          />
        </div>
      )}

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
          Save Changes
        </Button>
      </div>
    </form>
  );
};

// Gateway Configuration Form Component
const GatewayConfigForm = ({ gateway, onSave, onCancel }) => {
  const [formData, setFormData] = useState({});
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (gateway) {
      setIsActive(gateway.is_active || false);
      // Initialize form data based on gateway type
      if (gateway.credentials) {
        setFormData({});
      }
    }
  }, [gateway]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, isActive);
  };

  if (!gateway) return null;

  // Define credential fields for each gateway type
  const getCredentialFields = () => {
    switch(gateway.gateway_type) {
      case 'wise':
        return [
          { key: 'api_token', label: 'Wise API Token', type: 'password', required: true },
          { key: 'profile_id', label: 'Wise Profile ID', type: 'text', required: true },
          { key: 'api_base_url', label: 'API Base URL', type: 'text', required: false, placeholder: 'https://api.sandbox.transferwise.tech' }
        ];
      case 'stripe':
        return [
          { key: 'publishable_key', label: 'Publishable Key', type: 'text', required: true, placeholder: 'pk_test_...' },
          { key: 'secret_key', label: 'Secret Key', type: 'password', required: true, placeholder: 'sk_test_...' },
          { key: 'webhook_secret', label: 'Webhook Secret', type: 'password', required: false, placeholder: 'whsec_...' }
        ];
      case 'cybersource':
        return [
          { key: 'merchant_id', label: 'Merchant ID', type: 'text', required: true },
          { key: 'api_key', label: 'API Key', type: 'password', required: true },
          { key: 'shared_secret', label: 'Shared Secret', type: 'password', required: true }
        ];
      case 'plugnpay':
        return [
          { key: 'merchant_id', label: 'Merchant ID', type: 'text', required: true },
          { key: 'api_key', label: 'API Key', type: 'password', required: true }
        ];
      case 'currencycloud':
        return [
          { key: 'login_id', label: 'Login ID', type: 'text', required: true, placeholder: 'your_login_id' },
          { key: 'api_key', label: 'API Key', type: 'password', required: true },
          { key: 'environment', label: 'Environment', type: 'select', required: true, options: ['production', 'demo'], placeholder: 'Select environment' }
        ];
      default:
        return [];
    }
  };

  const fields = getCredentialFields();

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
        <p className="text-sm text-yellow-800">
          🔒 All credentials are encrypted with AES-256 before storage. Never share these values.
        </p>
      </div>

      <div className="flex items-center space-x-2 mb-4">
        <input
          type="checkbox"
          id="is_active"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="rounded border-gray-300"
        />
        <Label htmlFor="is_active" className="cursor-pointer">
          Set as active gateway after saving
        </Label>
      </div>

      {fields.map((field) => (
        <div key={field.key}>
          <Label>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          {field.type === 'select' ? (
            <Select 
              value={formData[field.key] || ''} 
              onValueChange={(value) => setFormData({...formData, [field.key]: value})}
              required={field.required}
            >
              <SelectTrigger>
                <SelectValue placeholder={field.placeholder || 'Select...'} />
              </SelectTrigger>
              <SelectContent>
                {field.options.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              type={field.type}
              placeholder={field.placeholder || ''}
              value={formData[field.key] || ''}
              onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
              required={field.required}
            />
          )}
          {field.key === 'api_base_url' && (
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to use default (sandbox URL)
            </p>
          )}
          {field.key === 'environment' && (
            <p className="text-xs text-gray-500 mt-1">
              Use 'demo' for testing, 'production' for live transactions
            </p>
          )}
        </div>
      ))}

      <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-4">
        <p className="text-xs text-blue-800">
          <strong>Note:</strong> Changes take effect immediately. Make sure to test the gateway configuration 
          in a sandbox environment before activating for production use.
        </p>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
          Save Configuration
        </Button>
      </div>
    </form>
  );
};

export default AdminDashboard;
